import React, {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { Camera, Vector3 } from 'three'
import { KVNavigationMode, KVUnitType } from 'types'

export interface Kestrel3DTilesSettings {
  units: KVUnitType
  measurementPrecision: number
  orbitSpeed: number
}

export interface VisibilityState {
  annotations: boolean
  historicalAnnotations: boolean
  measurements: boolean
  inspectionAreas: boolean
  planeIntersections: boolean
}

export interface FlightTarget {
  type: 'position' | 'cameraView'
  position: { x: number; y: number; z: number }
  rotation?: { x: number; y: number; z: number }
}

export interface Kestrel3DTilesContextType {
  camera: Camera | null
  setCamera: (camera: Camera | null) => void

  controls: unknown
  setControls: (controls: unknown) => void

  navigationMode: KVNavigationMode
  setNavigationMode: Dispatch<SetStateAction<KVNavigationMode>>

  visibilityState: VisibilityState
  setVisibilityState: Dispatch<SetStateAction<VisibilityState>>

  settings: Kestrel3DTilesSettings
  setSettings: Dispatch<SetStateAction<Kestrel3DTilesSettings>>

  zoomToLocation: (position: Vector3, target?: Vector3) => void

  flyToCamera: (
    position: Vector3,
    rotation: { x: number; y: number; z: number }
  ) => void

  tilesFolder: string
  setTilesFolder: Dispatch<SetStateAction<string>>

  flightTarget: FlightTarget | undefined
  setFlightTarget: Dispatch<SetStateAction<FlightTarget | undefined>>

  logCameraDebug: boolean
  setLogCameraDebug: Dispatch<SetStateAction<boolean>>
}

const Kestrel3DTilesContext = createContext<
  Kestrel3DTilesContextType | undefined
>(undefined)

export const useKestrel3DTiles = (): Kestrel3DTilesContextType | null => {
  const context = useContext(Kestrel3DTilesContext)
  return context || null
}

interface Kestrel3DTilesProviderProps {
  children: ReactNode
}

export const Kestrel3DTilesProvider: FC<Kestrel3DTilesProviderProps> = ({
  children,
}) => {
  const [camera, setCamera] = useState<Camera | null>(null)
  const [controls, setControls] = useState<unknown>(null)
  const [navigationMode, setNavigationMode] =
    useState<KVNavigationMode>('orbit')
  const [tilesFolder, setTilesFolder] = useState<string>('rgb')
  const [flightTarget, setFlightTarget] = useState<FlightTarget | undefined>(
    undefined
  )
  const [logCameraDebug, setLogCameraDebug] = useState(false)

  const [visibilityState, setVisibilityState] = useState<VisibilityState>({
    annotations: true,
    historicalAnnotations: true,
    measurements: true,
    inspectionAreas: true,
    planeIntersections: true,
  })

  const [settings, setSettings] = useState<Kestrel3DTilesSettings>({
    units: 'Meters',
    measurementPrecision: 3,
    orbitSpeed: 0.7,
  })

  const zoomToLocation = useCallback((position: Vector3) => {
    const target = {
      type: 'position' as const,
      position: { x: position.x, y: position.y, z: position.z },
    }
    setFlightTarget(target)
  }, [])

  const flyToCamera = useCallback(
    (position: Vector3, rotation: { x: number; y: number; z: number }) => {
      const target = {
        type: 'cameraView' as const,
        position: { x: position.x, y: position.y, z: position.z },
        rotation,
      }
      setFlightTarget(target)
    },
    []
  )

  const value = useMemo(
    () => ({
      camera,
      setCamera,
      controls,
      setControls,
      navigationMode,
      setNavigationMode,
      visibilityState,
      setVisibilityState,
      settings,
      setSettings,
      zoomToLocation,
      flyToCamera,
      tilesFolder,
      setTilesFolder,
      flightTarget,
      setFlightTarget,
      logCameraDebug,
      setLogCameraDebug,
    }),
    [
      camera,
      controls,
      navigationMode,
      visibilityState,
      settings,
      tilesFolder,
      flightTarget,
      logCameraDebug,
      zoomToLocation,
      flyToCamera,
    ]
  )

  return (
    <Kestrel3DTilesContext.Provider value={value}>
      {children}
    </Kestrel3DTilesContext.Provider>
  )
}
